// React and core functionality
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material-UI components
import {
  Grid,
  Box,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Typography,
  Switch,
  Stack,
  Button,
  Divider,
} from "@mui/material";

// Styling utilities
import { styled } from "@mui/material/styles";

// Icons
import ShareIcon from "@mui/icons-material/ShareOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import TimerIcon from "@mui/icons-material/TimerOffOutlined";

// Custom components
import AdvancedOptions from "../reusableComponent/AdvancedOptions";

const ToolbarContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 0),
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "1.1rem",
  color: theme.palette.text.primary,
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const LabelWithIcon = ({ icon, children }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    {icon}
    <Typography sx={{ fontSize: "0.82rem", fontWeight: 500 }}>{children}</Typography>
  </Stack>
);

// Known query keys (long + short forms)
const LONG_KEYS = [
  "model",
  "service",
  "team",
  "tone",
  "length",
  "len",
  "fullscreenMode",
  "writingStyle",
  "temperature",
  "language",
  "system",
  "chatId",
];
const SHORT_KEYS = ["m", "s", "tm", "tn", "ln", "fs", "ws", "tp", "lg", "sm", "cid"];

const AiChatSelection = ({
  input,
  setInput,
  skipLocalStorageHydration = false,
  linkHydrationOffer = null,
  onShareSetup,
  exposeParams,
  setExposeParams,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [shareBusy, setShareBusy] = useState(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Detect if URL contains our params (shared link case) and auto-enable toggle.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const hasAny =
      LONG_KEYS.some((k) => sp.has(k)) || SHORT_KEYS.some((k) => sp.has(k));
    if (hasAny && !exposeParams) setExposeParams(true);
    // eslint-disable-next-line
  }, [location.search]);

  const handleChangeTemporaryChat = (event) => {
    const isTemp = event.target.checked;
    setInput((prevInput) => ({
      ...prevInput,
      isTemporaryChat: isTemp,
    }));
  };

  const handleShareClick = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      await onShareSetup?.();
    } finally {
      setTimeout(() => setShareBusy(false), 500);
    }
  };

  const stripOurParamsFromUrl = () => {
    const sp = new URLSearchParams(location.search);
    LONG_KEYS.forEach((k) => sp.delete(k));
    SHORT_KEYS.forEach((k) => sp.delete(k));

    const nextSearch = sp.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  };

  const handleShowParamsToggle = (event) => {
    const next = event.target.checked;
    setExposeParams(next);

    // If turning OFF, remove params from URL immediately
    if (!next) {
      stripOurParamsFromUrl();
    }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Toolbar */}
        <Grid item xs={12}>
          <ToolbarContainer>
            <Title>Scribe Chat</Title>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                flex: "0 1 auto",
                minWidth: 0,
                flexWrap: "wrap",
                rowGap: 1,
              }}
              divider={
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" } }} // ✅ hide on xs
                />
              }
            >
              {/* Temporary Chat toggle */}
              <Tooltip
                title="When enabled, your chats won't be saved in Scribe. They are temporarily processed by the AI only to generate responses."
                placement="top"
              >
                <FormControlLabel
                  sx={{ m: 0, mr: { xs: 1, sm: 0 } }}
                  control={
                    <Switch
                      size="small"
                      checked={input.isTemporaryChat}
                      onChange={handleChangeTemporaryChat}
                      inputProps={{ "aria-label": "Temporary Chat" }}
                    />
                  }
                  label={<LabelWithIcon icon={<TimerIcon fontSize="small" />}>Temp</LabelWithIcon>}
                />
              </Tooltip>

              {/* Show params toggle */}
              <Tooltip
                title={
                  exposeParams
                    ? "Hide the current setup from your URL"
                    : "Show the current setup in your URL"
                }
                placement="top"
              >
                <FormControlLabel
                  sx={{ m: 0, mr: { xs: 1, sm: 0 } }}
                  control={
                    <Switch
                      size="small"
                      checked={!!exposeParams}
                      onChange={handleShowParamsToggle}
                      inputProps={{ "aria-label": "Show params in URL" }}
                    />
                  }
                  label={<LabelWithIcon icon={<LinkIcon fontSize="small" />}>Params</LabelWithIcon>}
                />
              </Tooltip>

              {/* Share button */}
              <Tooltip
                title="Copy a shareable link with your current setup. Tip: enable Temporary Chat first for safer sharing."
                placement="top"
              >
                <span>
                  <Button
                    size="small"
                    startIcon={<ShareIcon fontSize="small" />}
                    variant="outlined"
                    color="secondary"
                    onClick={handleShareClick}
                    disabled={shareBusy}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 1.25,
                      minHeight: 32,
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {shareBusy ? (isXs ? "…" : "Copying…") : (isXs ? "Share" : "Share")}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </ToolbarContainer>
        </Grid>

        {/* Advanced Options */}
        <Grid item xs={12}>
          <AdvancedOptions
            input={input}
            setInput={setInput}
            skipLocalStorageHydration={skipLocalStorageHydration}
            linkHydrationOffer={linkHydrationOffer}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AiChatSelection;
